/**
 * usePremiumTokens.js v2.0
 * Hook para acesso aos tokens premium com detecção automática de dark/light mode
 *
 * Uso:
 *   import { usePremiumTokens } from '../theme/usePremiumTokens';
 *
 *   const MyComponent = () => {
 *     const tokens = usePremiumTokens();
 *     return <div style={{ color: tokens.accent.primary }} />;
 *   };
 * 
 * Novos hooks em v2.0:
 *   - useButtonTokens() - tokens específicos de botões
 *   - useChartTokens() - tokens para gráficos
 *   - useCommandBarTokens() - tokens do command bar
 *   - useTripStatusTokens() - tokens de status de viagem
 */

import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  darkTokens,
  lightTokens,
  typography,
  spacing,
  radius,
  transitions,
  zIndex,
  breakpoints,
} from './premiumTokens';

// Cache estático dos tokens completos para evitar recriação
const cachedTokens = {
  dark: {
    ...darkTokens,
    typography,
    spacing,
    radius,
    transitions,
    zIndex,
    breakpoints,
    isDark: true,
    mode: 'dark',
  },
  light: {
    ...lightTokens,
    typography,
    spacing,
    radius,
    transitions,
    zIndex,
    breakpoints,
    isDark: false,
    mode: 'light',
  },
};

/**
 * Hook principal que retorna os tokens premium baseado no modo atual do tema
 * OTIMIZADO: Retorna referência estável do cache, evita recriação a cada render
 * @returns {Object} Tokens premium completos (dark ou light + shared)
 */
export const usePremiumTokens = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Retorna referência estável do cache - sem criar novo objeto
  return useMemo(() => isDark ? cachedTokens.dark : cachedTokens.light, [isDark]);
};

/**
 * Hook que retorna apenas os tokens de glassmorphism do modo atual
 * OTIMIZADO: Retorna referência do objeto original (sem spread)
 */
export const useGlassmorphism = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.glassmorphism : lightTokens.glassmorphism;
};

/**
 * Hook que retorna os tokens de status do modo atual
 * OTIMIZADO: Retorna referência do objeto original
 */
export const useStatusTokens = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.status : lightTokens.status;
};

/**
 * Hook que retorna os tokens de status de viagem/trip
 */
export const useTripStatusTokens = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.tripStatus : lightTokens.tripStatus;
};

/**
 * Hook que retorna os tokens de botões
 */
export const useButtonTokens = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.button : lightTokens.button;
};

/**
 * Hook que retorna os tokens de charts
 */
export const useChartTokens = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.chart : lightTokens.chart;
};

/**
 * Hook que retorna os tokens do Command Bar
 */
export const useCommandBarTokens = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.commandBar : lightTokens.commandBar;
};

/**
 * Hook que retorna os tokens do Activity Feed
 */
export const useActivityFeedTokens = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.activityFeed : lightTokens.activityFeed;
};

/**
 * Hook que retorna os tokens CRUD (formulários, tabelas, etc)
 */
export const useCrudTokens = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.crud : lightTokens.crud;
};

/**
 * Hook que retorna os tokens de componentes específicos
 */
export const useComponentTokens = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.components : lightTokens.components;
};

/**
 * Hook que retorna tokens semânticos (success, error, warning, info)
 */
export const useSemanticTokens = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.semantic : lightTokens.semantic;
};

/**
 * Hook que retorna apenas tokens de texto
 */
export const useTextTokens = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.text : lightTokens.text;
};

/**
 * Hook que retorna apenas tokens de borda
 */
export const useBorderTokens = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.border : lightTokens.border;
};

/**
 * Hook que retorna apenas tokens de sombra
 */
export const useShadowTokens = () => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkTokens.shadows : lightTokens.shadows;
};

/**
 * Hook utilitário para criar estilos de input premium
 * OTIMIZADO: Memoizado para evitar recriação
 * @returns {Object} Objeto com estilos MUI prontos para usar
 */
export const usePremiumInputStyles = () => {
  const tokens = usePremiumTokens();

  return useMemo(() => ({
    root: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: tokens.crud.form.inputBg,
        borderRadius: radius.md,
        transition: transitions.colors,

        '& fieldset': {
          borderColor: tokens.crud.form.inputBorder,
          transition: transitions.colors,
        },

        '&:hover fieldset': {
          borderColor: tokens.crud.form.inputBorderHover,
        },

        '&.Mui-focused fieldset': {
          borderColor: tokens.crud.form.inputBorderFocus,
          borderWidth: 2,
        },

        '&.Mui-error fieldset': {
          borderColor: tokens.crud.form.inputBorderError,
        },
      },

      '& .MuiOutlinedInput-input': {
        color: tokens.text.primary,

        '&::placeholder': {
          color: tokens.text.placeholder,
          opacity: 1,
        },
      },

      '& .MuiInputLabel-root': {
        color: tokens.crud.form.labelColor,

        '&.Mui-focused': {
          color: tokens.crud.form.labelFocusColor,
        },

        '&.Mui-error': {
          color: tokens.crud.form.errorText,
        },
      },

      '& .MuiFormHelperText-root': {
        color: tokens.crud.form.helperText,

        '&.Mui-error': {
          color: tokens.crud.form.errorText,
        },
      },
    },
  }), [tokens]);
};

/**
 * Hook utilitário para criar estilos de tabela premium
 * OTIMIZADO: Memoizado para evitar recriação
 * @returns {Object} Objeto com estilos MUI prontos para usar
 */
export const usePremiumTableStyles = () => {
  const tokens = usePremiumTokens();

  return useMemo(() => ({
    container: {
      backgroundColor: tokens.surface.elevated,
      borderRadius: radius.lg,
      border: `1px solid ${tokens.border.subtle}`,
      overflow: 'hidden',
    },
    header: {
      '& .MuiTableCell-head': {
        backgroundColor: tokens.crud.table.headerBg,
        color: tokens.crud.table.headerText,
        fontWeight: typography.fontWeight.semibold,
        fontSize: typography.fontSize.sm,
        borderBottom: `1px solid ${tokens.crud.table.borderColor}`,
        padding: `${spacing[3]} ${spacing[4]}`,
      },
    },
    row: {
      transition: transitions.colors,

      '&:hover': {
        backgroundColor: tokens.crud.table.rowHover,
      },

      '&.Mui-selected': {
        backgroundColor: tokens.crud.table.rowSelected,
      },

      '& .MuiTableCell-body': {
        color: tokens.text.primary,
        fontSize: typography.fontSize.md,
        borderBottom: `1px solid ${tokens.crud.table.borderColor}`,
        padding: `${spacing[3]} ${spacing[4]}`,
      },
    },
    rowAlt: {
      '&:nth-of-type(even)': {
        backgroundColor: tokens.crud.table.rowBgAlt,
      },
    },
  }), [tokens]);
};

/**
 * Hook utilitário para criar estilos de card premium
 * OTIMIZADO: Memoizado para evitar recriação
 * @returns {Object} Objeto com estilos prontos para usar
 */
export const usePremiumCardStyles = () => {
  const tokens = usePremiumTokens();

  return useMemo(() => ({
    root: {
      backgroundColor: tokens.surface.card,
      borderRadius: radius.lg,
      border: `1px solid ${tokens.border.subtle}`,
      transition: transitions.all,

      '&:hover': {
        backgroundColor: tokens.surface.cardHover,
        borderColor: tokens.border.default,
      },
    },
    selected: {
      backgroundColor: tokens.surface.selected,
      borderColor: tokens.border.accent,
    },
    clickable: {
      cursor: 'pointer',

      '&:active': {
        backgroundColor: tokens.surface.cardActive,
      },
    },
  }), [tokens]);
};

// Export default para conveniência
export default usePremiumTokens;
